import React, { useState, useRef, useEffect } from 'react';
import styles from './styles.css';
import { useTranslation } from 'react-i18next';

function CloseIcon({ className }) {
	return (
		<svg
			className={className}
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path d="M4 4L12 12M12 4L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
		</svg>
	);
}

function SearchIcon({ className }) {
	return (
		<svg
			className={className}
			width="16"
			height="16"
			viewBox="0 0 16 16"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M7 12A5 5 0 1 0 7 2a5 5 0 0 0 0 10ZM13 13l-2.35-2.35"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

export default function CountryMultiSelect({
	countryList = [],
	selectedCountries = [],
	onChange,
	placeholder = 'Search countries...',
	className = '',
}) {
	const { t } = useTranslation();
	const [isOpen, setIsOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const [focusedIndex, setFocusedIndex] = useState(-1);
	const containerRef = useRef(null);
	const inputRef = useRef(null);
	const dropdownRef = useRef(null);

	// Handle clicks outside to close dropdown
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (containerRef.current && !containerRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Filter countries based on search term
	const filteredCountries = countryList.filter((country) => {
		if (!searchTerm) return true;
		return country.fullName.toLowerCase().includes(searchTerm.toLowerCase());
	});

	// Get display name for a country
	const getCountryDisplayName = (iso) => {
		const country = countryList.find((c) => c.iso === iso);
		return country ? country.fullName : iso;
	};

	// Toggle country selection
	const toggleCountry = (countryIso) => {
		if (selectedCountries.includes(countryIso)) {
			onChange(selectedCountries.filter((iso) => iso !== countryIso));
		} else {
			onChange([...selectedCountries, countryIso]);
		}
	};

	// Remove country from selection
	const removeCountry = (countryIso, event) => {
		event.stopPropagation();
		onChange(selectedCountries.filter((iso) => iso !== countryIso));
	};

	// Clear search input
	const clearSearch = (event) => {
		event.stopPropagation();
		setSearchTerm('');
		inputRef.current?.focus();
	};

	// Handle keyboard navigation
	const handleKeyDown = (event) => {
		if (!isOpen && (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ')) {
			event.preventDefault();
			setIsOpen(true);
			return;
		}

		if (!isOpen) return;

		switch (event.key) {
			case 'ArrowDown':
				event.preventDefault();
				setFocusedIndex((prev) => Math.min(prev + 1, filteredCountries.length - 1));
				break;
			case 'ArrowUp':
				event.preventDefault();
				setFocusedIndex((prev) => Math.max(prev - 1, -1));
				break;
			case 'Enter':
			case ' ':
				event.preventDefault();
				if (focusedIndex >= 0 && focusedIndex < filteredCountries.length) {
					toggleCountry(filteredCountries[focusedIndex].iso);
				}
				break;
			case 'Escape':
				event.preventDefault();
				setIsOpen(false);
				inputRef.current?.focus();
				break;
			default:
				break;
		}
	};

	// Scroll focused item into view
	useEffect(() => {
		if (focusedIndex >= 0 && dropdownRef.current) {
			const items = dropdownRef.current.querySelectorAll(`.${styles.dropdownItem}`);
			if (items[focusedIndex]) {
				items[focusedIndex].scrollIntoView({ block: 'nearest' });
			}
		}
	}, [focusedIndex]);

	return (
		<div ref={containerRef} className={`${styles.multiSelectContainer} ${className}`}>
			<div
				className={`${styles.selectInput} ${isOpen ? styles.open : ''}`}
				onClick={() => {
					setIsOpen(!isOpen);
					inputRef.current?.focus();
				}}
				role="combobox"
				aria-expanded={isOpen}
				aria-haspopup="listbox"
				tabIndex={0}
				onKeyDown={handleKeyDown}
			>
				<div className={styles.selectedItems}>
					{selectedCountries.map((iso) => (
						<div key={iso} className={styles.selectedPill}>
							<img
								className={styles.pillFlag}
								src={`/img/roundflags/${iso.toLowerCase()}.svg`}
								alt={getCountryDisplayName(iso)}
							/>
							<span className={styles.pillText}>{getCountryDisplayName(iso)}</span>
							<button
								type="button"
								className={styles.pillRemove}
								onClick={(e) => removeCountry(iso, e)}
								title={t('Remove {{country}}', { country: getCountryDisplayName(iso) })}
								aria-label={t('Remove {{country}}', { country: getCountryDisplayName(iso) })}
								tabIndex={-1}
							>
								<CloseIcon className={styles.pillCloseIcon} />
							</button>
						</div>
					))}
					<input
						ref={inputRef}
						type="text"
						value={searchTerm}
						onChange={(e) => {
							setSearchTerm(e.target.value);
							setIsOpen(true);
							setFocusedIndex(-1);
						}}
						className={styles.searchInput}
						placeholder={placeholder}
						aria-label={t('Search countries')}
					/>
				</div>
				<div className={styles.searchIconContainer}>
					{searchTerm ? (
						<button
							type="button"
							className={styles.clearButton}
							onClick={clearSearch}
							title={t('Clear search')}
							aria-label={t('Clear search')}
							tabIndex={-1}
						>
							<CloseIcon className={styles.searchIcon} />
						</button>
					) : (
						<SearchIcon className={styles.searchIcon} />
					)}
				</div>
				<div className={styles.selectArrow}>
					<svg
						width="12"
						height="12"
						viewBox="0 0 12 12"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
						className={isOpen ? styles.arrowUp : ''}
					>
						<path
							d="M2 4L6 8L10 4"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</div>
			</div>

			{isOpen && (
				<div ref={dropdownRef} className={styles.dropdown} role="listbox">
					{filteredCountries.length === 0 ? (
						<div className={styles.noResults}>{t('No countries found')}</div>
					) : (
						filteredCountries.map((country, index) => {
							const isSelected = selectedCountries.includes(country.iso);
							const isFocused = index === focusedIndex;
							return (
								<div
									key={country.iso}
									className={`${styles.dropdownItem} ${isSelected ? styles.selected : ''} ${
										isFocused ? styles.focused : ''
									}`}
									onClick={() => toggleCountry(country.iso)}
									role="option"
									aria-selected={isSelected}
								>
									<input
										type="checkbox"
										checked={isSelected}
										onChange={() => {}}
										className={styles.checkbox}
										tabIndex={-1}
									/>
									<img
										className={styles.countryFlag}
										src={`/img/roundflags/${country.iso.toLowerCase()}.svg`}
										alt={country.fullName}
									/>
									<span className={styles.countryName}>{country.fullName}</span>
								</div>
							);
						})
					)}
				</div>
			)}
		</div>
	);
}
